export interface FAQItem {
  q: string;
  a: string;
}

export interface FAQSection {
  id:    string;
  icon:  string;
  title: string;
  items: FAQItem[];
}

export const FAQ_SECTIONS: FAQSection[] = [
  {
    id:    "overview",
    icon:  "fa-circle-info",
    title: "Ohio Talent Track Overview",
    items: [
      {
        q: "What is Ohio Talent Track?",
        a: "Ohio Talent Track is a platform created to give the Find Your Ohio team better visibility into what happens after a candidate enters the program and understand whether their referrals and program efforts were leading to real outcomes. It helps the team understand whether candidates are being referred, moving to Ohio, getting hired by Ohio employers, or being placed with referred companies.",
      },
      {
        q: "Where does the candidate journey begin?",
        a: "The journey begins when a candidate fills out the Find Your Ohio website form. After the form is submitted, the candidate's information goes into Tracker RMS. From there, the Find Your Ohio team can review the candidate and refer them to participating companies when there is a potential fit.",
      },
      {
        q: "What are the data sources for Ohio Talent Track?",
        a: "Tracker RMS is the data source where candidate information, company information, and referral details are stored before being brought into Ohio Talent Track.",
      },
      {
        q: "What outcomes does Ohio Talent Track help measure?",
        a: "Ohio Talent Track helps measure three main outcomes: Direct Placements, Influenced Relocations, and Influenced Placements.",
      },
      {
        q: "Who uses Ohio Talent Track?",
        a: "Ohio Talent Track is used by the Find Your Ohio team and approved JobsOhio users who need visibility into candidate tracking, referral activity, and program outcomes.",
      },
      {
        q: "Does Ohio Talent Track have the ability to track all candidates?",
        a: "No. A candidate can only be tracked if they have a usable LinkedIn profile. If LinkedIn is missing or the LinkedIn URL does not work, the candidate will be marked as untrackable.",
      },
      {
        q: "How long are candidates tracked?",
        a: "Candidates are tracked for up to 3 years. The tracking window depends on the outcome being measured. For Direct Placements, tracking begins from the referral date. For Influenced Relocation and Influenced Placement, tracking begins when the candidate enters Tracker RMS.",
      },
    ],
  },
  {
    id:    "dashboard",
    icon:  "fa-chart-bar",
    title: "AI Analytics Dashboard",
    items: [
      {
        q: "What information does the AI Analytics Dashboard show?",
        a: "AI Analytics shows high-level metrics such as total candidates, total referrals, candidates with LinkedIn profiles, actively tracked candidates, tracking expired candidates, and untrackable candidates.",
      },
      {
        q: "How often is the data refreshed?",
        a: "Candidate and referral information from Tracker RMS is refreshed into Ohio Talent Track daily. LinkedIn-based tracking is checked separately every 3 months.",
      },
      {
        q: "Why do some numbers change over time?",
        a: "Numbers may change as new candidates enter Tracker RMS, new referrals are created, LinkedIn profiles are updated, or candidates move in or out of the active tracking window.",
      },
      {
        q: "What does 'actively tracking' mean?",
        a: "Actively tracking means the candidate has a usable LinkedIn profile and is still within their 3-year tracking window.",
      },
      {
        q: "What does 'tracking expired' mean?",
        a: "Tracking expired means the candidate has reached the end of their 3-year tracking window. After that point, the candidate is no longer actively tracked for new outcomes.",
      },
      {
        q: "What does 'untrackable' mean?",
        a: "Untrackable means the candidate cannot currently be tracked through LinkedIn. This may happen because the LinkedIn field is blank, the LinkedIn URL is incorrect, or the LinkedIn profile cannot be accessed successfully.",
      },
    ],
  },
  {
    id:    "candidates",
    icon:  "fa-user",
    title: "Candidates",
    items: [
      {
        q: "Who is considered a candidate?",
        a: "A candidate is a person who fills out the Find Your Ohio web form and enters the Tracker RMS system.",
      },
      {
        q: "What information does a candidate provide?",
        a: "A candidate may provide their name, email, current job title, current employer, state, town or city, country, and LinkedIn profile.",
      },
      {
        q: "Is LinkedIn required when a candidate fills out the form?",
        a: "No. LinkedIn is optional on the Find Your Ohio web form. However, Ohio Talent Track needs a valid LinkedIn profile to track outcomes.",
      },
      {
        q: "Why are candidates tracked for 3 years?",
        a: "Candidates are tracked for 3 years to give enough time to identify whether they relocate to Ohio, join an Ohio employer, or are hired by a referred company.",
      },
      {
        q: "When does the 3-year tracking period begin?",
        a: "For relocation and influenced placement tracking, the 3-year period begins when the candidate fills the Find Your Ohio web form. For direct placement tracking, the 3-year period begins from the referral date.",
      },
      {
        q: "Can a candidate be referred to more than one company?",
        a: "Yes. One candidate can be referred to multiple companies. Each referral is counted separately.",
      },
      {
        q: "What is the difference between total candidates and total referrals?",
        a: "Total candidates count each individual person in Tracker RMS. Total referrals counts each candidate-company referral. For example, if one candidate is referred to five companies, that is one candidate and five referrals.",
      },
      {
        q: "Why can a candidate be untrackable?",
        a: "A candidate may be untrackable if they did not provide LinkedIn information, provided an incorrect LinkedIn URL, or if their LinkedIn profile cannot be successfully read.",
      },
      {
        q: "Can an untrackable candidate become trackable later?",
        a: "Yes. If a valid LinkedIn profile is added or corrected later, the candidate may become trackable again if they are still within the tracking window.",
      },
    ],
  },
  {
    id:    "direct-placements",
    icon:  "fa-briefcase",
    title: "Direct Placements",
    items: [
      {
        q: "What is a Direct Placement?",
        a: "A Direct Placement happens when a candidate is hired by the same company they were referred to by the Find Your Ohio team.",
      },
      {
        q: "How is a Direct Placement identified?",
        a: "Ohio Talent Track compares the referred company in Tracker RMS against the candidate's current employer on LinkedIn, requiring the referred company to have an active LinkedIn presence.",
      },
      {
        q: "What is an example of a Direct Placement?",
        a: "If candidate ABC is referred to Company 123, and LinkedIn later shows that ABC works at Company 123, that will be counted as a Direct Placement.",
      },
      {
        q: "Does a candidate count as a Direct Placement if they already worked at the company before the referral?",
        a: "No. The candidate must be hired during the tracking period after the referral date. If the candidate already worked at the company before the referral, it is not counted as a Direct Placement win.",
      },
      {
        q: "What is the Direct Placement tracking window?",
        a: "The tracking window is 3 years from the referral date.",
      },
      {
        q: "Why does Direct Placement tracking start from the referral date?",
        a: "Because Direct Placement is specifically measuring whether a referral led to a hire. The referral date is the starting point for measuring that outcome.",
      },
      {
        q: "Can one candidate have multiple Direct Placement records?",
        a: "A candidate can have multiple referrals, but each referral is reviewed separately against the referred company. The system checks whether the candidate was hired by the specific company tied to each referral.",
      },
    ],
  },
  {
    id:    "influenced-relocation",
    icon:  "fa-map-location-dot",
    title: "Influenced Relocation",
    items: [
      {
        q: "What is Influenced Relocation?",
        a: "Influenced Relocation means a candidate appears to have moved from outside Ohio into Ohio during the tracking period.",
      },
      {
        q: "Why is relocation important to Find Your Ohio?",
        a: "Find Your Ohio is focused on bringing talent into Ohio. Relocation tracking helps show whether candidates are moving into the state after entering the program.",
      },
      {
        q: "How is Influenced Relocation identified?",
        a: "Ohio Talent Track reviews the candidate's location information, such as state, city, or county, to identify whether the candidate has moved into Ohio.",
      },
      {
        q: "What is counted as a relocation win?",
        a: "A relocation win may be counted when a candidate who was originally outside Ohio later shows a location inside Ohio during the tracking window.",
      },
      {
        q: "When does the relocation tracking window begin?",
        a: "The relocation tracking window begins when the candidate enters Tracker RMS after submitting the Find Your Ohio web form.",
      },
      {
        q: "How long is a candidate tracked for relocation?",
        a: "A candidate is tracked for relocation for 3 years from the date they entered Tracker RMS.",
      },
      {
        q: "Does a candidate need to be referred to a company to count under Influenced Relocation?",
        a: "No. Influenced Relocation focuses on whether the candidate moved to Ohio, not whether they were referred to a specific company.",
      },
      {
        q: "Is Influenced Relocation the same as Direct Placement?",
        a: "No. Direct Placement checks whether a candidate joined the company they were referred to. Influenced Relocation checks whether the candidate moved into Ohio.",
      },
      {
        q: "Can a candidate be counted as relocated if they were already in Ohio?",
        a: "No. This section is intended to show movement from outside Ohio into Ohio.",
      },
      {
        q: "What if a candidate's location is missing or unclear?",
        a: "If the candidate's location cannot be confirmed, the system may not be able to count the candidate as relocated.",
      },
    ],
  },
  {
    id:    "influenced-placement",
    icon:  "fa-building",
    title: "Influenced Placement",
    items: [
      {
        q: "What is Influenced Placement?",
        a: "Influenced Placement means a candidate appears to have been hired by an Ohio-based employer during the tracking period.",
      },
      {
        q: "How is Influenced Placement different from Direct Placement?",
        a: "Direct Placement checks whether the candidate joined the specific company they were referred to. Influenced Placement checks whether the candidate joined any Ohio-based employer.",
      },
      {
        q: "What is an example of Influenced Placement?",
        a: "If a candidate previously worked for an out-of-state employer and later joins an Ohio-based employer, that will be counted as an Influenced Placement.",
      },
      {
        q: "Does the candidate need to be referred to that Ohio employer?",
        a: "No. For Influenced Placement, the candidate does not need to be referred to that specific employer. The outcome focuses on whether the candidate joined an Ohio employer.",
      },
      {
        q: "What is counted as an Influenced Placement win?",
        a: "A placement win may be counted when a candidate moves from an out-of-state employer to an Ohio-based employer during the tracking window.",
      },
      {
        q: "When does the Influenced Placement tracking window begin?",
        a: "The tracking window begins when the candidate enters Tracker RMS.",
      },
      {
        q: "How long is a candidate tracked for Influenced Placement?",
        a: "A candidate is tracked for three years from the date they entered Tracker RMS.",
      },
      {
        q: "Can a candidate be counted under Influenced Placement if they already worked for an Ohio employer before entering the program?",
        a: "No. The goal is to identify new movement into Ohio employment after the candidate enters the program.",
      },
      {
        q: "Why does Influenced Placement use LinkedIn?",
        a: "LinkedIn helps identify changes in a candidate's employer over time. This is currently the main available way to track whether a candidate has joined an Ohio-based employer.",
      },
    ],
  },
  {
    id:    "referral-companies",
    icon:  "fa-handshake",
    title: "Referral Companies",
    items: [
      {
        q: "What is a referral company?",
        a: "A referral company is a company that participates in the Find Your Ohio program and receives candidate referrals from the Find Your Ohio team.",
      },
      {
        q: "Where does referral company information come from?",
        a: "Referral company information comes from Tracker RMS.",
      },
      {
        q: "What does it mean when a candidate is referred to a company?",
        a: "It means the Find Your Ohio team identified the candidate as a potential fit and connected or submitted that candidate for consideration with a participating company.",
      },
      {
        q: "Can one candidate be referred to multiple companies?",
        a: "Yes. A single candidate can be referred to more than one company, and each referral is counted separately.",
      },
      {
        q: "Why are referrals counted separately?",
        a: "Each referral represents a separate opportunity for a candidate to be considered by a company. Counting them separately gives a clearer view of referral activity.",
      },
      {
        q: "What information is used to match a referral company to a candidate's LinkedIn employer?",
        a: "Ohio Talent Track uses the company information from Tracker RMS and compares it with the employer information found on the candidate's LinkedIn profile.",
      },
      {
        q: "Why is company LinkedIn information helpful?",
        a: "Company LinkedIn information can help improve matching accuracy, especially when company names are written differently across systems.",
      },
      {
        q: "Does a referral automatically mean the candidate was hired?",
        a: "No. A referral only means the candidate was shared with or connected to a company. A hire is only counted when the candidate appears to have joined that company during the tracking window.",
      },
      {
        q: "Why is referral company tracking important?",
        a: "Referral company tracking helps the Find Your Ohio team understand which referrals are turning into actual outcomes and where the program may be creating hiring impact.",
      },
    ],
  },
];
